/**
 * PATH: apps/quote/src/components/builder/scope-ag-grid/ScopeClientViewBanner.tsx
 * FILE_ID: QB-AGGRID-BANNER-001
 * PURPOSE: Green CLIENT VIEW strip listing the exact columns that
 *          will appear on the client-facing PDF, with a clear
 *          right-hand note that the other columns are internal-only.
 * WHY: SAM's screenshot shows this banner directly below the toolbar
 *      and it's one of the strongest "polish" signals — it tells the
 *      estimator at a glance which numbers are about to be exposed to
 *      the customer. Quotech had no equivalent.
 * FLOW: client column list (from quote config / hardcoded fallback)
 *       -> formatted as "Col · Col · Col" -> rendered as banner.
 * DEPENDENCIES: lucide-react (Eye icon)
 * RELATED:
 *   - QB-AGGRID-TOOLBAR-001 — sits directly above
 *   - QB-AGGRID-SECTION-001 — host
 */

import { Eye } from 'lucide-react'

export interface ScopeClientViewBannerProps {
  visibleColumnLabels: string[]
}

const FALLBACK_LABELS = ['Location', 'Description', 'Qty', 'Unit', 'Total']

export default function ScopeClientViewBanner({
  visibleColumnLabels,
}: ScopeClientViewBannerProps) {
  const labels =
    visibleColumnLabels.length > 0 ? visibleColumnLabels : FALLBACK_LABELS

  return (
    <div
      role="note"
      className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-1.5 text-[11px] border rounded-md bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900 text-emerald-800 dark:text-emerald-200"
    >
      <Eye className="w-3.5 h-3.5 shrink-0" />
      <span className="font-semibold tracking-wide uppercase whitespace-nowrap">
        Client view
      </span>
      <span className="opacity-80 whitespace-nowrap">
        columns shown to the client on the PDF —
      </span>
      <span className="font-medium">{labels.join(' · ')}</span>
      <span className="flex-1" />
      <span className="opacity-70 italic whitespace-nowrap">
        Other columns are internal estimating only.
      </span>
    </div>
  )
}
